/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useSwipeable } from 'react-swipeable';
import { GlobalFonts } from '../../styles/GlobalFonts';
import { fourPracticaLocales } from '../../locales/firstScreen';
import { Footer } from '../UI/Footer/Footer';

const LANG_KEY = 'stonpools-lang';

interface FourPractica2Props {
  onSwipeUp?: () => void;
  onSwipeLeft?: () => void;
  onSwipeDown?: () => void;
  onSwipeRight?: () => void;
  toHome?: () => void;
  handleDown?: () => void;
}

const slideVariants = {
  enter: {
    x: '100%',
  },
  center: {
    x: 0,
  },
  exit: {
    x: '-100%',
  },
};

const Container = styled.div`
  background: #151515;
  width: 100%;
  height: 100vh;
  padding: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const ScrollContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding-bottom: 100px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: calc(100% - 20px);
  height: 90px;
  margin: 50px auto 30px;
  padding: 13px;
  border-radius: 20px;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url(/assets/background.gif) center/cover no-repeat;
    mix-blend-mode: normal;
    opacity: 0.9;
    border-radius: 20px;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #7354f2;
    opacity: 1;
    mix-blend-mode: multiply;
    border-radius: 20px;
  }

  > * {
    position: relative;
    z-index: 2;
  }
`;

const Title = styled.h2`
  color: #ffffff;
  font-family: 'Manrope', sans-serif;
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 8px;
  margin-top: 1px;
  line-height: 100%;
`;

const PracticaButton = styled.button`
  background: none;
  border: 2px solid #ffffff;
  border-radius: 12px;
  padding: 5px 7px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-align: left;
  line-height: 100%;
`;

const Content = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 0 10px;
`;

const Text = styled.p`
  color: #b4b4b4;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  text-align: center;
  margin: 20px 0;
  line-height: 145%;
  flex-shrink: 0;
  white-space: pre-line;

  strong {
    color: #ffffff;
    font-weight: 700;
  }

  @media (min-height: 800px) {
    margin: 30px 20px;
  }

  @media (max-height: 600px) {
    margin: 15px 20px;
  }
`;

const QuestionBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  position: relative;
`;

const QuestionText = styled.span`
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: clamp(26px, 5vw, 26px);
  font-weight: 600;
  line-height: 90%;
  letter-spacing: -0.02em;
  text-align: center;
  white-space: pre-line;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HighlightBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 350px;
  min-height: 120px;
  margin: 20px auto;
  aspect-ratio: 3/1;
  box-sizing: border-box;

  @media (min-height: 800px) {
    max-width: 400px;
    margin: 30px auto;
  }

  @media (max-height: 600px) {
    max-width: 320px;
    margin: 15px auto;
  }
`;

const HighlightText = styled.span`
  color: #7354f2;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  white-space: pre-line;
  line-height: 1.4;
  padding: 0 15px;
  text-shadow:
    0 0 5px rgba(115, 84, 242, 0.4),
    0 0 10px rgba(115, 84, 242, 0.3),
    0 0 15px rgba(115, 84, 242, 0.2),
    0 0 20px rgba(115, 84, 242, 0.15),
    0 0 25px rgba(115, 84, 242, 0.1);
`;

const ScrollIcon = styled.img`
  width: clamp(16px, 5vw, 20px);
  height: clamp(32px, 8vh, 41px);
  margin: clamp(20px, 4vh, 60px) auto 20px;
  display: block;
  filter: hue-rotate(40deg) saturate(120%);

  @media screen and (max-height: 568px) {
    margin: 16px auto;
  }
`;

const HomeButtonIcon = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
`;

const HomeButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  margin: 0;
  background-color: transparent;
`;

const textAnimation = {
  hidden: {
    y: 50,
    opacity: 0,
  },
  visible: (custom: number) => ({
    y: 0,
    opacity: 1,
    transition: {
      delay: custom * 0.2,
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
    },
  }),
};
const AnimatedScrollIcon = motion(ScrollIcon);

const FourPractica2: React.FC<FourPractica2Props> = ({
  onSwipeUp,
  onSwipeLeft,
  onSwipeDown,
  onSwipeRight,
  toHome,
  handleDown,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [hasShownHint, setHasShownHint] = useState(false);

  const lang = (localStorage.getItem(LANG_KEY) as 'ru' | 'en') || 'ru';
  const t = fourPracticaLocales[lang]?.[2] || {};

  const textContent = React.useMemo(() => {
    if (lang === 'en' && typeof t.text === 'string') {
      const farmerTarget = 'Farmer’s Algorithm:';
      const farmTarget = 'Farm';

      const highlightTargets = (
        text: string,
        targets: string[]
      ): React.ReactNode[] => {
        const pieces: React.ReactNode[] = [text];

        targets.forEach((target) => {
          for (let i = 0; i < pieces.length; i += 1) {
            const node = pieces[i];
            if (typeof node !== 'string') continue;

            const segments: React.ReactNode[] = [];
            let startIndex = 0;
            let matchIndex = node.indexOf(target);

            while (matchIndex !== -1) {
              if (matchIndex > startIndex) {
                segments.push(node.slice(startIndex, matchIndex));
              }

              segments.push(
                <strong key={`${target}-${matchIndex}`}>{target}</strong>
              );

              startIndex = matchIndex + target.length;
              matchIndex = node.indexOf(target, startIndex);
            }

            if (startIndex < node.length) {
              segments.push(node.slice(startIndex));
            }

            if (segments.length > 0) {
              pieces.splice(i, 1, ...segments);
              i += segments.length - 1;
            }
          }
        });

        return pieces;
      };

      return <>{highlightTargets(t.text, [farmerTarget, farmTarget])}</>;
    }

    return t.text;
  }, [lang, t.text]);

  const handleScroll = () => {
    if (!contentRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
    const isBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 10;
    setIsAtBottom(isBottom);
  };

  const handlers = useSwipeable({
    onSwipedUp: () => {
      // Упростим логику: если скролл внизу — всегда вызываем onSwipeUp
      if (!contentRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      const isBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 10;
      if (isBottom) {
        if (onSwipeUp) {
          onSwipeUp();
        }
      } else {
        contentRef.current.scrollTo({
          top: contentRef.current.scrollHeight,
          behavior: 'smooth',
        });
        setHasShownHint(true);
      }
    },
    onSwipedDown: () => {
      if (onSwipeDown) {
        onSwipeDown();
      }
    },
    onSwipedRight: () => onSwipeRight?.(),
    onSwipedLeft: () => onSwipeLeft?.(),
    preventScrollOnSwipe: false,
    trackMouse: true,
    delta: 50,
    swipeDuration: 500,
  });

  useEffect(() => {
    const content = contentRef.current;
    if (content) {
      content.addEventListener('scroll', handleScroll);
      // Проверяем скролл при маунте (если мало контента)
      handleScroll();
      return () => content.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const handleExternalLink = () => {
    window.open('https://app.ston.fi/farms', '_blank');
  };

  return (
    <motion.div
      initial="enter"
      animate="center"
      exit="exit"
      variants={slideVariants}
      transition={{ duration: 0.3 }}
      {...handlers}
    >
      <GlobalFonts />
      <Container>
        <ScrollContainer ref={contentRef}>
          <HomeButton onClick={toHome}>
            <HomeButtonIcon src="/assets/homeIconPurple.svg" />
          </HomeButton>
          <Header>
            <Title>{t.title}</Title>
            <PracticaButton>{t.practica}</PracticaButton>
          </Header>
          <QuestionBlock>
            <QuestionText>{t.question}</QuestionText>
          </QuestionBlock>
          <Content>
            <Text>{textContent}</Text>
            <HighlightBox>
              <HighlightText>{t.highlight}</HighlightText>
            </HighlightBox>
            <Footer textRight={t.next} onSwipeLeft={onSwipeLeft} />
          </Content>
        </ScrollContainer>
      </Container>
    </motion.div>
  );
};

export default FourPractica2;
